import React, { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { Palette } from '../../components/icons';
import PaletteCaseGallery from '../../components/PaletteCaseGallery';
import { getContrastRatio } from '../../utils/colorAnalysis';
import { hexToRgb, rgbToHex, rgbToHsl, hslToRgb, getNearestColorName } from '../../utils/colors';
import { updateThemePalettesMetadata } from '../../utils/seo';

const ThemePalettesPage: React.FC = () => {
  const { hex } = useParams<{ hex: string }>();


  const baseColor = useMemo(() => {
    if (!hex) return { r: 255, g: 107, b: 107 }; // Default fallback
    return hexToRgb('#' + hex) || { r: 255, g: 107, b: 107 };
  }, [hex]);

  useEffect(() => {
    if (hex) {
      const colorName = getNearestColorName(baseColor.r, baseColor.g, baseColor.b);
      updateThemePalettesMetadata(hex, colorName);
    }
  }, [hex, baseColor]);

  const palettes = useMemo(() => {
    const hsl = rgbToHsl(baseColor.r, baseColor.g, baseColor.b);

    // Blend a theme's fixed hue with the user's base hue (80% theme, 20% base)
    const blendHue = (themeHue: number): number => {
      // Handle hue wrap-around by using shortest angular distance
      const diff = ((themeHue - hsl.h + 540) % 360) - 180;
      return ((hsl.h + diff * 0.8) + 360) % 360;
    };

    const generateColor = (h: number, s: number, l: number) => {
      const rgb = hslToRgb((h + 360) % 360, Math.max(0, Math.min(100, s)), Math.max(0, Math.min(100, l)));
      return rgbToHex(rgb.r, rgb.g, rgb.b);
    };

    const createPalette = (name: string, description: string, colors: string[]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      description,
      colors
    });

    // Expanded list of themes (20+ variations)
    return [
      // 1. Core Themes (from PopularPalettes)
      createPalette('Pastel Dreams', 'Soft, desaturated hues for a calming and gentle aesthetic.', [
        generateColor(hsl.h, 50, 92),
        generateColor(hsl.h + 30, 50, 88),
        generateColor(hsl.h + 180, 40, 92),
        generateColor(hsl.h + 210, 40, 88),
        generateColor(hsl.h + 60, 45, 90)
      ]),
      createPalette('Vintage Retro', 'Muted and warm tones inspired by 70s and 80s design.', [
        generateColor(hsl.h, 40, 60),
        generateColor(hsl.h + 30, 30, 70),
        generateColor(hsl.h + 15, 20, 80),
        generateColor(hsl.h - 30, 40, 50),
        generateColor(hsl.h + 180, 25, 40)
      ]),
      createPalette('Neon Cyberpunk', 'High saturation and contrast for a futuristic, digital look.', [
        generateColor(hsl.h, 100, 60),
        generateColor(hsl.h + 180, 100, 60),
        generateColor(hsl.h + 90, 100, 60),
        generateColor(hsl.h - 90, 100, 60),
        '#121212'
      ]),
      createPalette('Dark Mode', 'Dark surface candidates. Add and test lighter text roles before using them in an interface.', [
        generateColor(hsl.h, 30, 20),
        generateColor(hsl.h, 20, 15),
        generateColor(hsl.h + 180, 30, 25),
        generateColor(hsl.h, 10, 10),
        generateColor(hsl.h + 60, 20, 18)
      ]),
      createPalette('Warm Sunset', 'Inviting reds, oranges, and yellows evoking evening light.', [
        generateColor(blendHue(30), 70, 60),
        generateColor(blendHue(50), 80, 70),
        generateColor(blendHue(10), 60, 50),
        generateColor(blendHue(70), 90, 80),
        generateColor(blendHue(40), 85, 90)
      ]),
      createPalette('Cold Breeze', 'Cool blues and cyans for a refreshing and clean feeling.', [
        generateColor(blendHue(210), 60, 70),
        generateColor(blendHue(240), 50, 80),
        generateColor(blendHue(180), 70, 60),
        generateColor(blendHue(210), 30, 90),
        generateColor(blendHue(30), 20, 95)
      ]),
      createPalette('Summer Vibes', 'Bright and vibrant colors representing energy and sunshine.', [
        generateColor(hsl.h, 80, 60),
        generateColor(hsl.h + 120, 70, 70),
        generateColor(hsl.h + 240, 70, 70),
        generateColor(hsl.h + 60, 80, 80),
        '#FFFFFF'
      ]),
      createPalette('Autumn Fall', 'Earthy browns and oranges reflecting the changing seasons.', [
        generateColor(blendHue(30), 60, 40),
        generateColor(blendHue(40), 70, 50),
        generateColor(blendHue(20), 50, 30),
        generateColor(blendHue(45), 40, 80),
        generateColor(blendHue(120), 30, 35)
      ]),

      // 2. Nature Themes
      createPalette('Forest Walk', 'Deep greens and browns inspired by woodland environments.', [
        generateColor(blendHue(120), 40, 30),
        generateColor(blendHue(140), 30, 40),
        generateColor(blendHue(100), 50, 20),
        generateColor(blendHue(40), 40, 80), // Wood/Bark
        generateColor(blendHue(120), 20, 90) // Mist
      ]),
      createPalette('Ocean Depth', 'Various shades of blue representing the sea and sky.', [
        generateColor(blendHue(220), 60, 30),
        generateColor(blendHue(200), 70, 40),
        generateColor(blendHue(180), 50, 50),
        generateColor(blendHue(240), 40, 20),
        generateColor(blendHue(200), 20, 90) // Foam
      ]),
      createPalette('Desert Sands', 'Warm neutrals and terracottas from arid landscapes.', [
        generateColor(blendHue(35), 40, 70),
        generateColor(blendHue(30), 50, 60),
        generateColor(blendHue(25), 60, 50),
        generateColor(blendHue(40), 30, 80),
        generateColor(blendHue(200), 40, 80) // Sky contrast
      ]),
      createPalette('Spring Bloom', 'Fresh pastels and floral tones for new beginnings.', [
        generateColor(blendHue(330), 60, 80), // Pink
        generateColor(blendHue(120), 50, 80), // Green
        generateColor(blendHue(60), 70, 80),  // Yellow
        generateColor(blendHue(280), 40, 80), // Lavender
        generateColor(blendHue(330), 20, 95)
      ]),

      // 3. Food & Drink
      createPalette('Coffee House', 'Rich browns and creams for a cozy, cafe atmosphere.', [
        generateColor(blendHue(25), 40, 20), // Espresso
        generateColor(blendHue(30), 30, 40), // Mocha
        generateColor(blendHue(35), 20, 60), // Latte
        generateColor(blendHue(40), 10, 85), // Milk
        generateColor(blendHue(15), 40, 30)  // Bean
      ]),
      createPalette('Berry Smoothie', 'Juicy purples and pinks for a sweet and energetic vibe.', [
        generateColor(blendHue(330), 60, 40),
        generateColor(blendHue(300), 50, 50),
        generateColor(blendHue(350), 70, 60),
        generateColor(blendHue(280), 40, 40),
        generateColor(blendHue(330), 20, 90)
      ]),
      createPalette('Citrus Burst', 'Zesty oranges, yellows, and limes for high energy.', [
        generateColor(blendHue(30), 90, 60), // Orange
        generateColor(blendHue(50), 90, 60), // Yellow
        generateColor(blendHue(80), 80, 60), // Lime
        generateColor(blendHue(10), 80, 60), // Grapefruit
        '#FFFFFF'
      ]),

      // 4. Modern / Tech
      createPalette('Minimalist Grey', 'Clean greyscale tones for modern, structured interfaces.', [
        generateColor(hsl.h, 5, 20),
        generateColor(hsl.h, 5, 40),
        generateColor(hsl.h, 5, 60),
        generateColor(hsl.h, 5, 80),
        generateColor(hsl.h, 5, 95)
      ]),
      createPalette('Corporate Blue', 'Blue and neutral candidates for a restrained visual theme.', [
        generateColor(blendHue(220), 60, 30),
        generateColor(blendHue(220), 50, 50),
        generateColor(blendHue(220), 40, 70),
        generateColor(blendHue(220), 10, 90),
        generateColor(blendHue(30), 80, 60) // Accent Orange
      ]),
      createPalette('Material Design', 'Bold, flat color candidates inspired by a material-like visual style; not a validated Material token system.', [
        generateColor(hsl.h, 70, 50),
        generateColor(hsl.h, 70, 70),
        generateColor(hsl.h, 70, 30),
        generateColor(hsl.h + 180, 60, 50), // Accent
        '#FFFFFF'
      ]),
      createPalette('Synthwave', 'Retrowave aesthetics with neon pinks, purples, and cyans.', [
        '#' + (hex || 'ff00ff'), // Base color replaces Magenta
        '#00ffff', // Cyan
        '#ffff00', // Yellow
        '#240046', // Deep Purple
        '#000000'
      ]),
      createPalette('Pop Art', 'High contrast primary colors inspired by comic book art.', [
        '#' + (hex || 'ff0000'), // Base color replaces Red
        generateColor(60, 90, 60),  // Yellow
        generateColor(240, 80, 60), // Blue
        '#000000',
        '#FFFFFF'
      ])
    ];
  }, [baseColor]);

  return <Layout maxWidth="max-w-6xl"><PageHeader icon={Palette} title={`Theme palettes for #${hex?.toUpperCase()}`} description="These palettes are generated from your base hue and theme rules. They are starting points, not prevalidated text combinations. Each card checks its first swatch as text on its last swatch; open a palette to assign roles and inspect all pairs." /><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{palettes.map(palette => { const ratio = getContrastRatio(palette.colors[0], palette.colors[4]); return <Link key={palette.id} to={'/palette/' + palette.colors.map(c => c.slice(1)).join('-')} className="ui-card-link overflow-hidden"><div className="h-24 flex" aria-hidden="true">{palette.colors.map((color, i) => <div key={i} className="flex-1" style={{ background: color }}/>)}</div><div className="p-5"><h2 className="text-lg font-semibold">{palette.name}</h2><p className="text-sm text-ink-secondary mt-2">{palette.description}</p><p className="text-xs mt-4 font-mono">{palette.colors[0]} on {palette.colors[4]}</p><p className="text-sm mt-1">{ratio.toFixed(3)}:1 - {ratio >= 4.5 ? 'Pass' : 'Fail'} normal text AA</p><span className="inline-block text-sm text-brand-600 underline mt-4">Assign roles and inspect palette</span></div></Link>; })}</div><PaletteCaseGallery/></Layout>;
};
export default ThemePalettesPage;
